import { useClientRole } from "@/components/client-role";
import { EditPreviewToggle } from "@/components/dashboard/edit-preview-toggle";
import { EditableField, MiniSelect } from "@/components/dashboard/editable";
import { StatusPill } from "@/components/dashboard/status-pill";
import { useEditPreview } from "@/components/edit-preview";
import { files } from "@/lib/admin-api";
import { clientApi } from "@/lib/client-api";
import { STALL_DAYS, formatDate, formatMoney, relativeDays } from "@/lib/format";
import { cn, formatFileSize } from "@/lib/utils";
import {
  type CandidateCard,
  type CandidateStatus,
  INTERVIEW_FORMAT_LABELS,
  type InterviewFormat,
  type ProfileDocument,
  type ProfileEdge,
  type ProfileInterview,
  type StageDef,
  type Tier,
} from "@ow/shared";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  MapPin,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Upload,
  Users,
  Video,
  X,
} from "lucide-react";
import { DropdownMenu as DM, Dialog as DialogPrimitive } from "radix-ui";
import { type FormEvent, useEffect, useRef, useState } from "react";

/** Humanized document-type labels (the wire value is a raw enum like "offer_letter"). */
const DOC_TYPE_LABEL: Record<string, string> = {
  cv: "CV",
  assessment: "Assessment",
  reference: "Reference",
  offer_letter: "Offer letter",
  other: "Document",
};

/** A labelled section — the ONE section pattern used EVERYWHERE in the drawer (every card on both tabs).
 *  The label sits ABOVE the panel as an eyebrow (mono uppercase), NOT inside it, with an optional
 *  right-aligned data-tally (`meta`, e.g. a count) on the same row; the bounded `rounded-xl border bg-card`
 *  panel below holds only the content. Every titled card MUST go through here so the treatment can't drift
 *  between tabs (this is exactly the header the user wanted consistent — header out, card clean). */
function Section({ label, meta, children }: { label: string; meta?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2.5 flex items-baseline justify-between gap-3 px-1">
        <span className="eyebrow">{label}</span>
        {meta}
      </div>
      <section className="rounded-xl border bg-card p-4">{children}</section>
    </div>
  );
}

/** Two-letter initials from a person / role label ("Board Chair" → "BC", "CEO" → "CE"). Single-word
 *  labels (titles like CEO/CTO) take their first two letters so the disc never reads as a lone glyph. */
function initials(name: string) {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
  return parts
    .slice(0, 2)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();
}

/** The search firm's own side of the table — the people running the process, not a client-company evaluator.
 *  The client cares about who from *their* company assessed the person, so firm touchpoints are shown
 *  quietly and kept out of the company panel. Heuristic on the free-text label (v1 has no structured
 *  actor — interviewers are plain strings; see InterviewEntrySchema). */
function isSearchFirm(name: string) {
  return /search\s*(team|firm|partner)|oliver\s*wyman/i.test(name);
}

/** A single evaluator — initials disc + who they were. No pill chrome (the bordered pills read as
 *  tappable filters and boxed the panel in); just the face and the name, so a round's panel scans as a
 *  short list of people. Company evaluators read solid (filled disc, foreground name); the search firm's
 *  own people read quiet (outline disc, muted name) so "who from the client assessed them" stays the
 *  figure and the firm stays ground — without hiding firm-run rounds entirely. */
function Evaluator({ name, firm }: { name: string; firm: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={cn(
          "grid size-6 shrink-0 place-items-center rounded-full text-[10px] font-semibold",
          firm ? "border border-border text-muted-foreground" : "bg-secondary text-secondary-foreground",
        )}
      >
        {initials(name)}
      </span>
      <span
        className={cn(
          "whitespace-nowrap text-sm leading-none",
          firm ? "text-muted-foreground" : "font-medium text-foreground",
        )}
      >
        {name}
      </span>
    </span>
  );
}

/** Lift the medium out of an interview's free-text name when the name redundantly ends with it
 *  ("Round 1 — Panel" → "Round 1") — the format is shown once, as metadata, never twice. */
function stripFormat(iv: ProfileInterview): string {
  const label = iv.format ? INTERVIEW_FORMAT_LABELS[iv.format] : null;
  if (!label) return iv.roundName;
  return iv.roundName.replace(new RegExp(`\\s*[—–-]\\s*${label}\\s*$`, "i"), "").trim() || iv.roundName;
}

/** Phase-1 heuristic: map a free-text round name to the ladder stage key it belongs to, or null for an
 *  off-ladder touchpoint (screening call, informal debrief) that maps to no rung. Phase 2 replaces this
 *  guessing with an explicit stageKey stored on the interview. */
const OFF_LADDER = /screening|debrief|informal|intro\b|coffee|chemistry|catch[- ]?up|touchpoint/i;
const STAGE_CUES: { test: RegExp; key: string }[] = [
  { test: /round\s*1|first\s*round/i, key: "round_1" },
  { test: /round\s*2|second\s*round/i, key: "round_2" },
  { test: /round\s*3|third\s*round/i, key: "round_3" },
  { test: /final|last\s*round/i, key: "final" },
  { test: /reference/i, key: "reference_check" },
  { test: /brief/i, key: "briefing" },
  { test: /long\s*list/i, key: "longlist" },
  { test: /offer/i, key: "offer" },
  { test: /placed|hired|accepted/i, key: "placed" },
];
function matchStageKey(roundName: string, ladder: StageDef[]): string | null {
  if (OFF_LADDER.test(roundName)) return null;
  const keys = new Set(ladder.map((s) => s.key));
  for (const cue of STAGE_CUES) {
    if (cue.test.test(roundName) && keys.has(cue.key)) return cue.key;
  }
  const n = roundName.toLowerCase();
  const byLabel = ladder.find((s) => n.includes(s.label.toLowerCase()));
  return byLabel ? byLabel.key : null;
}

/** The tone the current node carries — the candidate's standing read as an honest exit state. */
function currentTone(status: CandidateStatus): string | undefined {
  return status === "placed"
    ? "var(--color-success)"
    : status === "rejected"
      ? "var(--color-destructive)"
      : status === "on_hold"
        ? "var(--color-warning)"
        : status === "withdrawn"
          ? undefined
          : "var(--brand-accent)";
}

// --- DEMO MOCK: per-stage completion dates + descriptors ------------------------------------------
// The model only dates interview rounds (interview_log). Gate stages (briefing, references, offer…) and
// any round with no logged interview carry no date or note anywhere, so those rungs read bare next to the
// evaluated ones. Until stage-transition history lands (Phase 2), we synthesize a believable completion
// date per completed stage — anchored to the REAL interview dates and the current-stage entry, so the
// sequence stays chronological — plus a short descriptor for the gate stages. Swap this block for the
// real stage history and the render below is unchanged.
// Gate stages aren't interviews, so they carry a short status line instead of an evaluation.
const STAGE_NOTE: Record<string, string> = {
  briefing: "Client reviewed the profile and cleared the candidate to interview.",
  reference_check: "Referees contacted; checks in progress.",
  offer: "Offer package finalised and extended.",
  placed: "Offer accepted — start date confirmed.",
};
function isRoundKey(key: string): boolean {
  return key.startsWith("round_") || key === "final";
}

// A passed interview round with no logged interview would otherwise render bare next to the evaluated ones.
// In demo mode we synthesize a full evaluation for it — same shape as a real one (format · date, evaluators,
// feedback) — keyed per round so every interview rung shares ONE layout. Replace with real data in Phase 2.
const MOCK_EVAL: Record<string, { format: InterviewFormat; panel: string[]; feedback: string }> = {
  round_1: {
    format: "panel",
    panel: ["Hiring Manager", "Line Executive"],
    feedback: "Assured on the fundamentals; the panel agreed to progress.",
  },
  round_2: {
    format: "video",
    panel: ["Audit Committee Chair", "Group CEO"],
    feedback: "Credible with the board and sharp on the numbers — unanimous to advance.",
  },
  round_3: {
    format: "panel",
    panel: ["Division Head", "CFO"],
    feedback: "Strong functional depth; clear fit with the leadership team.",
  },
  final: {
    format: "in_person",
    panel: ["CEO", "Board Member"],
    feedback: "Convincing across the room; recommended to move to references.",
  },
};
function synthEval(stageKey: string, label: string, dateMs: number | null, searchId: string): ProfileInterview | null {
  const m = MOCK_EVAL[stageKey];
  if (!m) return null;
  return {
    id: `mock-${searchId}-${stageKey}`,
    roundName: label,
    dateConducted: dateMs != null ? new Date(dateMs).toISOString() : null,
    interviewers: m.panel,
    format: m.format,
    recommendation: "proceed",
    feedbackNotes: m.feedback,
    searchId,
    searchTitle: "",
  };
}
/** Fill a completion date (epoch ms) for every stage up to and including the current one: real interview
 *  dates are the anchors, the current stage is anchored to its entry (`now − daysInStage`), and the gaps
 *  are interpolated between anchors (or stepped a week apart past the ends). Upcoming stages stay null. */
function buildStageDates(
  ladder: StageDef[],
  curIdx: number,
  byStage: Map<string, ProfileInterview[]>,
  daysInStage: number | null,
): (number | null)[] {
  const DAY = 86_400_000;
  const known: (number | null)[] = new Array(ladder.length).fill(null);
  for (let i = 0; i <= curIdx; i++) {
    const ds = (byStage.get(ladder[i]?.key ?? "") ?? [])
      .map((e) => (e.dateConducted ? Date.parse(e.dateConducted) : Number.NaN))
      .filter((x) => !Number.isNaN(x));
    if (ds.length) known[i] = Math.min(...ds);
  }
  const currentEntry = daysInStage != null ? Date.now() - daysInStage * DAY : Date.now();
  if (curIdx >= 0 && known[curIdx] == null) known[curIdx] = currentEntry;

  const STEP = 7 * DAY;
  const out: (number | null)[] = known.slice();
  for (let i = 0; i <= curIdx; i++) {
    if (out[i] != null) continue;
    let p = i - 1;
    while (p >= 0 && out[p] == null) p--;
    let q = i + 1;
    while (q <= curIdx && known[q] == null) q++;
    const prev = p >= 0 ? out[p] : null;
    const next = q <= curIdx ? known[q] : null;
    if (prev != null && next != null) out[i] = Math.round(prev + ((next - prev) * (i - p)) / (q - p));
    else if (next != null) out[i] = next - (q - i) * STEP;
    else if (prev != null) out[i] = prev + (i - p) * STEP;
    else out[i] = currentEntry - (curIdx - i) * STEP;
  }
  return out;
}

/** Format → a glyph, so the medium of each round registers before the label does. */
const FORMAT_ICON: Record<InterviewFormat, typeof Video> = {
  video: Video,
  in_person: MapPin,
  panel: Users,
};

/** The HOW · WHEN metadata of one evaluation, right-anchored — format glyph + label, a middot, the date.
 *  The same shape on every row so the panel scans cleanly. */
function EvalMeta({ iv }: { iv: ProfileInterview }) {
  const Icon = iv.format ? FORMAT_ICON[iv.format] : null;
  const label = iv.format ? INTERVIEW_FORMAT_LABELS[iv.format] : null;
  if (!label && !iv.dateConducted) return null;
  return (
    <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
      {label ? (
        <span className="flex items-center gap-1">
          {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden /> : null}
          {label}
        </span>
      ) : null}
      {label && iv.dateConducted ? <span aria-hidden>·</span> : null}
      {iv.dateConducted ? <span className="tabular-nums">{formatDate(iv.dateConducted)}</span> : null}
    </div>
  );
}

/** WHO assessed the candidate + their written feedback — the body shared by every logged evaluation.
 *  Company evaluators read solid and lead; the search firm's own people trail and read quiet. */
function EvalBody({ iv }: { iv: ProfileInterview }) {
  const evaluators = [
    ...iv.interviewers.filter((n) => !isSearchFirm(n)).map((name) => ({ name, firm: false })),
    ...iv.interviewers.filter(isSearchFirm).map((name) => ({ name, firm: true })),
  ];
  return (
    <>
      {evaluators.length > 0 ? (
        <div className="mt-3">
          <span className="eyebrow">Evaluated by</span>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            {evaluators.map((e) => (
              <Evaluator key={e.name} name={e.name} firm={e.firm} />
            ))}
          </div>
        </div>
      ) : null}
      {iv.feedbackNotes ? (
        <p className="mt-3 whitespace-pre-wrap border-l-2 border-border/70 pl-3 text-[0.8125rem] leading-relaxed text-foreground/80">
          {iv.feedbackNotes}
        </p>
      ) : null}
    </>
  );
}

/** One node on the journey spine. Completion is the signal: every finished pipeline stage is a solid
 *  green dot (interview or not), an off-ladder touchpoint is a dashed green ring, the current stage
 *  carries the standing tone, and stages not yet reached are hollow. */
function TimelineDot({ variant, tone }: { variant: "done" | "current" | "upcoming" | "off"; tone?: string }) {
  if (variant === "current") {
    return (
      <span
        className={cn("size-2.5 shrink-0 rounded-full", !tone && "bg-muted-foreground/50")}
        style={
          tone
            ? { backgroundColor: tone, boxShadow: `0 0 0 4px color-mix(in oklab, ${tone} 20%, transparent)` }
            : undefined
        }
      />
    );
  }
  if (variant === "off") {
    // A completed-but-off-ladder touchpoint: the SAME green as a done rung, dialled down — clearly on the
    // "achieved" side of the ledger, just not a formal pipeline stage. Distinct from both the solid done
    // dot and the hollow not-yet-reached one.
    return (
      <span
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: "color-mix(in oklab, var(--color-success) 38%, transparent)" }}
      />
    );
  }
  if (variant === "done") {
    return <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: "var(--color-success)" }} />;
  }
  return <span className="size-2.5 shrink-0 rounded-full border border-border bg-transparent" />;
}

// Shared trigger styles for the move menu — the compact ⋯ chip (when a primary sits beside it) and the
// full-width bordered button (when the menu is the only control).
const MOVE_ICON_TRIGGER =
  "grid size-8 shrink-0 place-items-center rounded-md border bg-card text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted data-[state=open]:text-foreground";
const MOVE_FULL_TRIGGER =
  "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border bg-card px-3 text-xs font-medium text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted";

/** Terminal candidate statuses close the pipeline — no forward advance is valid from any of them (a rejected
 *  or withdrawn person isn't going onward; a placed one has arrived). */
const TERMINAL_STATUSES = new Set<CandidateStatus>(["rejected", "withdrawn", "placed"]);
const isTerminalStatus = (s: CandidateStatus) => TERMINAL_STATUSES.has(s);
const OUTCOME_VERB: Record<CandidateStatus, string> = {
  active: "Active",
  on_hold: "On hold",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
  placed: "Placed",
};
const OUTCOME_TONE: Record<CandidateStatus, string> = {
  active: "text-foreground",
  on_hold: "text-[var(--color-warning)]",
  withdrawn: "text-muted-foreground",
  rejected: "text-[var(--color-destructive)]",
  placed: "text-[var(--color-success)]",
};

/** The "move to any other stage" menu — a deliberate, click-to-open list for the rare backward jump, skip,
 *  or correction. Shared by the normal advance control AND the outcome controls, so the correction path is
 *  identical everywhere and a mistake is always reversible. The trigger is passed in so each caller styles
 *  its own button. */
function StageMoveMenu({
  ladder,
  currentKey,
  onSetStage,
  children,
}: {
  ladder: StageDef[];
  currentKey: string;
  onSetStage: (key: string) => void;
  children: React.ReactNode;
}) {
  const others = ladder.filter((s) => s.key !== currentKey);
  if (others.length === 0) return null;
  return (
    <DM.Root>
      <DM.Trigger asChild>{children}</DM.Trigger>
      <DM.Portal>
        <DM.Content
          align="end"
          sideOffset={6}
          className="z-50 w-56 rounded-lg border bg-popover p-1.5 text-popover-foreground shadow-lg outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
        >
          <div className="field-label px-2 py-1">Move to</div>
          {others.map((s) => (
            <DM.Item
              key={s.key}
              onSelect={() => onSetStage(s.key)}
              className="flex w-full cursor-pointer items-center rounded-md px-2 py-1.5 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-accent"
            >
              {s.label}
            </DM.Item>
          ))}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

/** Moving a candidate's stage is a consequential write, so it is NOT a hover button on every rung (too easy
 *  to misclick while scanning). It lives once, inside the live stage's open panel: a primary "Advance to the
 *  next stage" (the 90% action, plainly labelled) plus a nested ⋯ menu for the rare jump backward or skip.
 *  Both take a deliberate click and never sit under the cursor as you read the timeline. */
function MoveControls({
  ladder,
  currentKey,
  onSetStage,
}: {
  ladder: StageDef[];
  currentKey: string;
  onSetStage: (key: string) => void;
}) {
  const idx = ladder.findIndex((s) => s.key === currentKey);
  const next = idx >= 0 && idx < ladder.length - 1 ? ladder[idx + 1] : null;
  const others = ladder.filter((s) => s.key !== currentKey);
  if (others.length === 0) return null;
  return (
    // The primary button stretches to fill the row so there's no dead space on the right; the ⋯ (or, at the
    // last stage where there's nothing to advance to, a full-width "Move to…") sits beside it.
    <div className="flex items-center gap-1.5">
      {next ? (
        <button
          type="button"
          onClick={() => onSetStage(next.key)}
          className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring"
        >
          Advance to {next.label} <ArrowRight className="size-3.5" />
        </button>
      ) : null}
      <StageMoveMenu ladder={ladder} currentKey={currentKey} onSetStage={onSetStage}>
        {next ? (
          <button type="button" aria-label="Move to a different stage" className={MOVE_ICON_TRIGGER}>
            <MoreHorizontal className="size-4" />
          </button>
        ) : (
          <button type="button" className={MOVE_FULL_TRIGGER}>
            Move to a different stage
          </button>
        )}
      </StageMoveMenu>
    </div>
  );
}

/** The move controls, made state-aware — a candidate can't be pushed forward out of a state that contradicts
 *  it. A REJECT recommendation on the live round replaces "Advance" with "Mark rejected"; a HOLD disables
 *  advancing until it's resolved; and a terminal status (rejected / withdrawn / placed) closes the journey
 *  outright — no advance, just the stated outcome and a reopen. Every path still routes through the same move
 *  menu, so nothing is a dead end. Edit-mode only (both callbacks are wired only for admins). */
function StageOutcome({
  ladder,
  currentKey,
  status,
  rec,
  onSetStage,
  onSetStatus,
}: {
  ladder: StageDef[];
  currentKey: string;
  status: CandidateStatus;
  rec: string;
  onSetStage: (key: string) => void;
  onSetStatus?: (s: CandidateStatus) => void;
}) {
  const idx = ladder.findIndex((s) => s.key === currentKey);
  const next = idx >= 0 && idx < ladder.length - 1 ? ladder[idx + 1] : null;
  const iconTrigger = (
    <button type="button" aria-label="Move to a different stage" className={MOVE_ICON_TRIGGER}>
      <MoreHorizontal className="size-4" />
    </button>
  );

  // Terminal — the pipeline is closed. State the outcome; offer a reopen and the correction menu, never a
  // forward advance.
  if (isTerminalStatus(status)) {
    const stageLabel = ladder.find((s) => s.key === currentKey)?.label ?? "";
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
        <span className="text-xs">
          <span className={cn("font-medium", OUTCOME_TONE[status])}>{OUTCOME_VERB[status]}</span>
          <span className="text-muted-foreground"> at {stageLabel} · pipeline closed</span>
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {onSetStatus ? (
            <button
              type="button"
              onClick={() => onSetStatus("active")}
              className="inline-flex h-7 items-center rounded-md px-2 text-xs font-medium text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              Reopen
            </button>
          ) : null}
          <StageMoveMenu ladder={ladder} currentKey={currentKey} onSetStage={onSetStage}>
            {iconTrigger}
          </StageMoveMenu>
        </div>
      </div>
    );
  }

  // A reject recommendation on the live round — the forward action becomes closing the candidate out.
  if (rec === "reject") {
    return (
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onSetStatus?.("rejected")}
          className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-[color-mix(in_oklab,var(--color-destructive)_35%,transparent)] bg-[color-mix(in_oklab,var(--color-destructive)_10%,transparent)] px-3 text-xs font-medium text-[var(--color-destructive)] outline-none transition-colors hover:bg-[color-mix(in_oklab,var(--color-destructive)_16%,transparent)] focus-visible:ring-2 focus-visible:ring-ring"
        >
          Mark rejected
        </button>
        <StageMoveMenu ladder={ladder} currentKey={currentKey} onSetStage={onSetStage}>
          {iconTrigger}
        </StageMoveMenu>
      </div>
    );
  }

  // A hold recommendation — advancing is blocked until it's resolved, but a manual move/correction stays
  // available through the menu.
  if (rec === "hold") {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            disabled
            className="inline-flex h-8 flex-1 cursor-not-allowed items-center justify-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background opacity-40"
          >
            {next ? `Advance to ${next.label}` : "Advance"}
          </button>
          <StageMoveMenu ladder={ladder} currentKey={currentKey} onSetStage={onSetStage}>
            {iconTrigger}
          </StageMoveMenu>
        </div>
        <p className="px-0.5 text-[11px] text-muted-foreground">
          On hold at this round — resolve the recommendation before advancing.
        </p>
      </div>
    );
  }

  // Proceed / no exception — the normal advance control.
  return <MoveControls ladder={ladder} currentKey={currentKey} onSetStage={onSetStage} />;
}

/** The Journey tab as ONE stage-by-stage spine (Phase 1 — display-only, no schema change). The role's
 *  ladder is the backbone; each interview is matched to the rung it belongs to and rendered inline, so a
 *  client reads position and assessment in a single column. Rungs with no interview state their standing
 *  through the node + a light body (source on the long-list, "in progress" on the current one); touch-
 *  points that map to no rung (screening calls) sit off-ladder with a dashed marker. */
function UnifiedJourney({
  ladder,
  currentKey,
  status,
  daysInStage,
  stalled,
  interviews,
  source,
  editing = false,
  onSetStage,
  onSetStatus,
}: {
  ladder: StageDef[];
  currentKey: string | null;
  status: CandidateStatus;
  daysInStage: number | null;
  stalled: boolean;
  interviews: ProfileInterview[];
  source: string | null;
  editing?: boolean;
  onSetStage?: (key: string) => void;
  onSetStatus?: (s: CandidateStatus) => void;
}) {
  const curIdx = currentKey ? ladder.findIndex((s) => s.key === currentKey) : -1;
  const byDate = (a: ProfileInterview, b: ProfileInterview) =>
    (a.dateConducted ? Date.parse(a.dateConducted) : 0) - (b.dateConducted ? Date.parse(b.dateConducted) : 0);

  const byStage = new Map<string, ProfileInterview[]>();
  const offLadder: ProfileInterview[] = [];
  for (const iv of interviews) {
    const key = matchStageKey(iv.roundName, ladder);
    if (key) {
      const arr = byStage.get(key) ?? [];
      arr.push(iv);
      byStage.set(key, arr);
    } else {
      offLadder.push(iv);
    }
  }

  const stageDates = buildStageDates(ladder, curIdx, byStage, daysInStage);

  // Demo mock: a completed interview round with no logged interview gets a synthesized evaluation so it
  // renders identically to the rounds that do have one (evaluators + feedback), not as a bare note.
  const searchId = interviews[0]?.searchId ?? "mock";
  for (let i = 0; i < curIdx; i++) {
    const s = ladder[i];
    if (s && isRoundKey(s.key) && !byStage.get(s.key)?.length) {
      const ev = synthEval(s.key, s.label, stageDates[i] ?? null, searchId);
      if (ev) byStage.set(s.key, [ev]);
    }
  }

  type Row =
    | {
        kind: "stage";
        key: string;
        label: string;
        state: "done" | "current" | "upcoming";
        evals: ProfileInterview[];
        date: number | null;
      }
    | { kind: "off"; iv: ProfileInterview };

  const stageRows: Row[] = ladder.map((s, i) => {
    const evals = (byStage.get(s.key) ?? []).slice().sort(byDate);
    const state: "done" | "current" | "upcoming" =
      i === curIdx ? "current" : i < curIdx || evals.length > 0 ? "done" : "upcoming";
    return {
      kind: "stage",
      key: s.key,
      label: s.label,
      state,
      evals,
      date: state === "upcoming" ? null : (stageDates[i] ?? null),
    };
  });

  // Off-ladder touchpoints have no rung — drop them in just above the first stage that carries an
  // evaluation (after the early gates, before the interview rounds), ordered by date.
  let insertAt = stageRows.findIndex((r) => r.kind === "stage" && r.evals.length > 0);
  if (insertAt < 0) insertAt = curIdx >= 0 ? curIdx : stageRows.length;
  const offRows: Row[] = offLadder.sort(byDate).map((iv) => ({ kind: "off", iv }));
  const rows: Row[] = [...stageRows.slice(0, insertAt), ...offRows, ...stageRows.slice(insertAt)];

  const tone = currentTone(status);
  // A terminal candidate (rejected / withdrawn / placed) has no live "in progress" stage — the current rung
  // states the outcome instead, and the move controls close rather than advance.
  const terminal = isTerminalStatus(status);

  // Hybrid disclosure state: only the collapsible (completed / off-ladder, non-exception) rungs are keyed
  // here. The current rung and any Hold/Reject stay open and locked, independent of this set.
  const [open, setOpen] = useState<Set<string>>(() =>
    editing && curIdx >= 0 ? new Set([`stage-${ladder[curIdx]?.key ?? ""}`]) : new Set(),
  );
  // The live recommendation the admin has picked for the CURRENT round (null = untouched, falls back to the
  // persisted one). It gates the move controls: a Reject/Hold here changes what the live stage can do next.
  const [liveRec, setLiveRec] = useState<string | null>(null);
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // The live stage is the focus: whenever it changes (Advance / Move), collapse everything and open just the
  // new current rung, so its editor + move controls are right there and old panels don't pile up behind it.
  useEffect(() => {
    if (editing && currentKey) setOpen(new Set([`stage-${currentKey}`]));
    // A new live stage starts with a clean recommendation slate (the prior stage's Reject/Hold shouldn't
    // carry over and keep the advance blocked).
    setLiveRec(null);
  }, [editing, currentKey]);

  return (
    <Section
      label="Progress"
      meta={
        curIdx >= 0 ? (
          <span className="eyebrow tabular-nums">
            Stage {curIdx + 1} of {ladder.length}
          </span>
        ) : undefined
      }
    >
      <ol>
        {rows.map((row, i) => {
          const last = i === rows.length - 1;
          const isOff = row.kind === "off";
          const id = isOff ? `off-${row.iv.id}` : `stage-${row.key}`;
          const evals = isOff ? [row.iv] : row.evals;
          const single = evals.length === 1 ? (evals[0] ?? null) : null;
          const exceptionRec =
            evals.find((e) => e.recommendation === "hold" || e.recommendation === "reject")?.recommendation ?? null;
          const dotVariant: "done" | "current" | "upcoming" | "off" = isOff ? "off" : row.state;
          const isCurrent = dotVariant === "current";
          const isUpcoming = dotVariant === "upcoming";
          const inProgress = isCurrent && evals.length === 0 && !terminal;
          const name = isOff ? stripFormat(row.iv) : row.label;
          const date = isOff ? null : row.date;

          const note =
            !isOff && evals.length === 0 && !inProgress && !isUpcoming ? (STAGE_NOTE[row.key] ?? "Completed.") : null;
          const sourceBody = !isOff && row.key === "longlist" && evals.length === 0 && source ? source : null;

          // In edit mode every rung the candidate has REACHED is editable — rounds open the interview form,
          // gates open a short status note — with the CURRENT stage open by default so the live stage is the
          // focus (it's what an admin is actually working). Past stages stay editable on click for the rare
          // correction; upcoming rungs stay inert until the candidate is moved there.
          const isRoundRow = isOff || isRoundKey(row.key);
          const rowStageKey = isOff ? null : row.key;
          const editable = editing && !isUpcoming;
          const locked = isCurrent || exceptionRec != null;
          const hasBody = evals.length > 0 || note != null || sourceBody != null;
          const collapsible = editable ? true : !locked && !isUpcoming && hasBody;
          const expanded = editable ? open.has(id) : locked || (collapsible && open.has(id));

          const rightMeta = single ? (
            <EvalMeta iv={single} />
          ) : isCurrent && terminal ? (
            <span className={cn("shrink-0 text-xs font-medium", OUTCOME_TONE[status])}>{OUTCOME_VERB[status]}</span>
          ) : inProgress ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              In progress
              {daysInStage != null ? (
                <>
                  {" · "}
                  <span className={cn(stalled && "text-[var(--color-warning)]")}>
                    {relativeDays(daysInStage)} in stage
                  </span>
                </>
              ) : null}
            </span>
          ) : evals.length === 0 && date != null ? (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {formatDate(new Date(date).toISOString())}
            </span>
          ) : null;

          const bodyContent = sourceBody ? (
            <div>
              <span className="eyebrow">Source</span>
              <p className="mt-1.5 text-sm text-foreground">{sourceBody}</p>
            </div>
          ) : note ? (
            <p className="border-l-2 border-border/70 pl-3 text-[0.8125rem] leading-relaxed text-foreground/80">
              {note}
            </p>
          ) : single ? (
            <EvalBody iv={single} />
          ) : evals.length > 1 ? (
            evals.map((iv, j) => (
              <div key={iv.id} className={j === 0 ? "" : "mt-4"}>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{stripFormat(iv)}</span>
                    {iv.recommendation === "hold" || iv.recommendation === "reject" ? (
                      <StatusPill value={iv.recommendation} />
                    ) : null}
                  </div>
                  <EvalMeta iv={iv} />
                </div>
                <EvalBody iv={iv} />
              </div>
            ))
          ) : null;

          const clickProps = collapsible
            ? {
                role: "button" as const,
                tabIndex: 0,
                "aria-expanded": expanded,
                onClick: () => toggle(id),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(id);
                  }
                },
              }
            : {};

          return (
            <li key={id} className="flex gap-3.5">
              <div className="flex flex-col items-center pt-2.5">
                <TimelineDot variant={dotVariant} tone={tone} />
                {last ? null : <span className="mt-1.5 w-px flex-1 bg-border" />}
              </div>
              <div
                className={cn(
                  "min-w-0 flex-1 rounded-lg",
                  last ? "" : "mb-0.5",
                  isCurrent && "bg-[color-mix(in_oklab,var(--brand-accent)_7%,transparent)]",
                )}
              >
                <div
                  className={cn(
                    "group/row rounded-lg outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                    collapsible && "cursor-pointer hover:bg-muted/60",
                  )}
                  {...clickProps}
                >
                  <div className="flex items-baseline justify-between gap-3 px-2.5 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <h4
                        className={cn(
                          "truncate text-sm font-medium leading-snug",
                          isUpcoming ? "text-muted-foreground" : "text-foreground",
                        )}
                      >
                        {name}
                      </h4>
                      {exceptionRec ? <StatusPill value={exceptionRec} /> : null}
                      {editable ? (
                        <Pencil
                          aria-hidden
                          className={cn(
                            "size-3.5 shrink-0 text-muted-foreground transition-opacity",
                            expanded ? "opacity-100" : "opacity-0 group-hover/row:opacity-100",
                          )}
                        />
                      ) : collapsible ? (
                        <ChevronDown
                          aria-hidden
                          className={cn(
                            "size-3.5 shrink-0 text-muted-foreground transition-all",
                            expanded ? "opacity-100" : "-rotate-90 opacity-0 group-hover/row:opacity-100",
                          )}
                        />
                      ) : null}
                    </div>
                    {rightMeta}
                  </div>
                </div>
                {expanded && (editable || bodyContent) ? (
                  <div className={cn("px-2.5 pb-3", editable && "pt-1")}>
                    {editable ? (
                      // The editor sits in its own panel, separated from the rung header above. Rounds carry
                      // the full interview form; gates (References, Offer, …) carry a short status note.
                      <div className="rounded-lg border bg-muted/30 p-3">
                        {isRoundRow ? (
                          <div className="space-y-5">
                            {evals.length > 0 ? (
                              evals.map((e) => (
                                <RoundEditor
                                  key={e.id}
                                  seed={{
                                    roundName: stripFormat(e),
                                    dateConducted: e.dateConducted,
                                    format: e.format,
                                    interviewers: e.interviewers,
                                    feedbackNotes: e.feedbackNotes,
                                    recommendation: e.recommendation,
                                  }}
                                  onRecChange={isCurrent ? setLiveRec : undefined}
                                />
                              ))
                            ) : (
                              <RoundEditor
                                seed={{ roundName: name }}
                                onRecChange={isCurrent ? setLiveRec : undefined}
                              />
                            )}
                          </div>
                        ) : (
                          <GateNoteEditor seed={note ?? ""} stageKey={rowStageKey ?? ""} />
                        )}
                      </div>
                    ) : (
                      bodyContent
                    )}
                    {/* Stage moves live ONLY on the live stage's panel — advance from where the candidate is,
                        or use the ⋯ menu to jump. No per-rung buttons, so nothing sits under the cursor. */}
                    {isCurrent && onSetStage && rowStageKey ? (
                      <div className="mt-2.5">
                        <StageOutcome
                          ladder={ladder}
                          currentKey={rowStageKey}
                          status={status}
                          rec={liveRec ?? exceptionRec ?? "proceed"}
                          onSetStage={onSetStage}
                          onSetStatus={onSetStatus}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
      {/* No standalone "Log interview" CTA: a dashed button below the spine read as "add a step to the
          pipeline," which it isn't. Interviews are logged ON the round they belong to — click that round's
          rung and it opens the editor in place. The rungs ARE the logging surface. */}
    </Section>
  );
}

/** A downloadable document row (flush, full-bleed hover). CV floats to the top of the list. */
function DocRow({ d }: { d: ProfileDocument }) {
  return (
    <a
      href={files.downloadUrl(d.id)}
      target="_blank"
      rel="noreferrer"
      className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2 text-sm outline-none transition-colors hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
        {DOC_TYPE_LABEL[d.docType] ?? d.docType}
      </span>
      {d.version > 1 ? <span className="shrink-0 text-[10px] text-muted-foreground">v{d.version}</span> : null}
      <span className="min-w-0 flex-1 truncate font-medium group-hover:underline">{d.fileName}</span>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatFileSize(d.byteSize)}</span>
      <Download className="size-4 shrink-0 text-muted-foreground/70" aria-hidden />
    </a>
  );
}

/** A compact reference to another of the client's roles this candidate also sits in — the role, their
 *  position in it, and status. Informational: the current role is the drawer's whole context, so these
 *  just tell the sponsor "you're also seeing this person elsewhere." */
function OtherRoleRow({ e }: { e: ProfileEdge }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="min-w-0 truncate text-sm font-medium text-foreground">{e.searchTitle}</span>
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-xs text-muted-foreground">{e.stageLabel}</span>
        <StatusPill value={e.status} />
      </div>
    </div>
  );
}

const STATUS_OPTIONS: { value: CandidateStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On hold" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "rejected", label: "Rejected" },
  { value: "placed", label: "Placed" },
];

/** A trailing text control for a Section header (upload, link role, …) — passed as the Section `meta`. */
function SectionAction({ icon: Icon, label, onClick }: { icon: typeof Plus; label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon className="size-3.5" /> {label}
    </button>
  );
}

/** Document row in edit mode — download stays, plus a remove affordance (a div, not the read-only
 *  anchor, so the controls aren't illegally nested inside a link). */
function EditableDocRow({ d, onRemove }: { d: ProfileDocument; onRemove: () => void }) {
  return (
    <div className="group flex items-center gap-3 rounded-lg px-1 py-2 text-sm transition-colors hover:bg-muted/60">
      <span className="shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
        {DOC_TYPE_LABEL[d.docType] ?? d.docType}
      </span>
      {d.version > 1 ? <span className="shrink-0 text-[10px] text-muted-foreground">v{d.version}</span> : null}
      <span className="min-w-0 flex-1 truncate font-medium">{d.fileName}</span>
      <a
        href={files.downloadUrl(d.id)}
        target="_blank"
        rel="noreferrer"
        aria-label="Download"
        className="shrink-0 text-muted-foreground/70 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Download className="size-4" />
      </a>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove document"
        className="shrink-0 text-muted-foreground/70 outline-none transition-colors hover:text-[var(--color-destructive)] focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}

/** The document types a file can be filed under — mirrors the DocType enum, sentence-cased for the picker. */
const DOC_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "cv", label: "CV" },
  { value: "assessment", label: "Assessment" },
  { value: "reference", label: "Reference" },
  { value: "offer_letter", label: "Offer letter" },
  { value: "other", label: "Other" },
];

/** Upload a document to the candidate — pick a real file (name + size are read client-side), tag its type,
 *  and it lands in the Documents list. Rendered INLINE inside the Documents section (no modal-over-drawer
 *  third surface). The file isn't persisted here (storage is a dev follow-up); the added row reads exactly
 *  like a real one. */
function UploadDocForm({
  onAdd,
  onClose,
}: {
  onAdd: (v: { fileName: string; byteSize: number; docType: string }) => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState("cv");
  const valid = file != null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    onAdd({ fileName: file.name, byteSize: file.size, docType });
    onClose();
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <span className="eyebrow">New document</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel"
          className="grid size-6 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-4" />
        </button>
      </div>
      <input ref={inputRef} type="file" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      {file ? (
        <div className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-sm">
          <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate font-medium text-foreground">{file.name}</span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatFileSize(file.size)}</span>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="shrink-0 text-xs font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            Replace
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center gap-1.5 rounded-lg border border-dashed border-border py-6 text-xs font-medium text-muted-foreground outline-none transition-colors hover:border-foreground/40 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Upload className="size-5" aria-hidden />
          Choose a file to upload
        </button>
      )}
      <div className="space-y-1.5">
        <span className="field-label">Document type</span>
        <MiniSelect
          ariaLabel="Document type"
          value={docType}
          onChange={setDocType}
          options={DOC_TYPE_OPTIONS}
          fullWidth
        />
      </div>
      <div className="flex justify-end pt-0.5">
        <button
          type="submit"
          disabled={!valid}
          className="inline-flex h-9 items-center rounded-md bg-foreground px-3.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
        >
          Upload
        </button>
      </div>
    </form>
  );
}

/** Link this candidate to another of the client's open roles — the graph model is one person ↔ many
 *  searches, so the same person can run in several pipelines at once. The picker offers every open role
 *  they're NOT already in; on link they enter at the top of that role's pipeline. Not persisted here (the
 *  real edge is a dev follow-up); the linked row reads exactly like a real "Other roles" entry. */
function LinkRoleForm({
  linkedIds,
  onLink,
  onClose,
}: {
  linkedIds: Set<string>;
  onLink: (v: { searchId: string; searchTitle: string; searchTier: Tier }) => void;
  onClose: () => void;
}) {
  // The ⌘K role roster — already fetched + cached elsewhere, so this reuses it rather than refetching.
  const { data } = useQuery({
    queryKey: ["client", "search-index"],
    queryFn: clientApi.searchIndex,
    staleTime: 60_000,
  });
  const available = (data?.roles ?? []).filter((r) => r.status !== "closed" && !linkedIds.has(r.id));
  const [roleId, setRoleId] = useState("");
  // Fall back to the first available role until one is picked, so the picker is never empty-valued.
  const selected = available.find((r) => r.id === roleId) ?? available[0] ?? null;
  const valid = selected != null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    onLink({ searchId: selected.id, searchTitle: selected.title, searchTier: selected.tier });
    onClose();
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <span className="eyebrow">Link to a role</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel"
          className="grid size-6 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-4" />
        </button>
      </div>
      {available.length === 0 ? (
        <p className="rounded-lg border border-dashed bg-card px-3 py-4 text-center text-sm text-muted-foreground">
          This candidate is already in every open role.
        </p>
      ) : (
        <div className="space-y-1.5">
          <span className="field-label">Role</span>
          <MiniSelect
            ariaLabel="Role"
            value={selected?.id ?? ""}
            onChange={setRoleId}
            options={available.map((r) => ({ value: r.id, label: r.title }))}
            fullWidth
          />
        </div>
      )}
      <div className="flex justify-end pt-0.5">
        <button
          type="submit"
          disabled={!valid}
          className="inline-flex h-9 items-center rounded-md bg-foreground px-3.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
        >
          Link role
        </button>
      </div>
    </form>
  );
}

const INTERVIEW_FORMAT_OPTIONS = [
  { value: "panel", label: "Panel" },
  { value: "video", label: "Video" },
  { value: "in_person", label: "In person" },
];
const RECOMMENDATION_OPTIONS = [
  { value: "proceed", label: "Proceed" },
  { value: "hold", label: "Hold" },
  { value: "reject", label: "Reject" },
];

// Tailored placeholders so a gate's note reads as "what's the status here," not a blank box.
const GATE_NOTE_HINT: Record<string, string> = {
  longlist: "Sourcing notes — e.g. shortlisted from 40 profiles",
  briefing: "Briefing notes — e.g. client cleared to interview",
  reference_check: "Reference-check progress — e.g. 2 of 3 back, positive",
  offer: "Offer status — e.g. sent 5 Jul, awaiting a decision",
  placed: "Start date / onboarding notes",
};

/** The inline editor for a GATE stage (Long List, Client Briefing, References, Offer, Hired) — these carry
 *  no interview, so instead of the round form they get one free-text status note for what's happening at the
 *  stage. Opened in place when the rung is expanded in edit mode. */
function GateNoteEditor({ seed, stageKey }: { seed: string; stageKey: string }) {
  const [note, setNote] = useState(seed);
  return (
    <textarea
      className="w-full resize-none rounded-md border bg-background px-2.5 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
      rows={3}
      value={note}
      onChange={(e) => setNote(e.target.value)}
      placeholder={GATE_NOTE_HINT[stageKey] ?? "What's happening at this stage?"}
      aria-label="Stage status note"
    />
  );
}

/** The inline editor for ONE interview, revealed in place when a round is opened in edit mode — the same
 *  timeline the client reads, now writable at the row. Captures exactly what the client reads back:
 *  round, date, format, panel, feedback, recommendation. */
function RoundEditor({
  seed,
  onRecChange,
}: {
  seed?: {
    roundName?: string;
    dateConducted?: string | null;
    format?: InterviewFormat | null;
    interviewers?: string[];
    feedbackNotes?: string | null;
    recommendation?: string | null;
  };
  /** Lifts the round's live recommendation up so the stage's move controls can react (Reject/Hold gate the
   *  advance). Only the current round wires this. */
  onRecChange?: (rec: string) => void;
}) {
  const [name, setName] = useState(seed?.roundName ?? "");
  const [date, setDate] = useState(seed?.dateConducted ? formatDate(seed.dateConducted) : "");
  const [format, setFormat] = useState<string>(seed?.format ?? "panel");
  const [people, setPeople] = useState((seed?.interviewers ?? []).join(", "));
  const [feedback, setFeedback] = useState(seed?.feedbackNotes ?? "");
  const [rec, setRec] = useState<string>(seed?.recommendation ?? "proceed");
  const field =
    "w-full rounded-md border bg-background px-2.5 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <div className="space-y-2.5">
      <input
        className={field}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Round name — e.g. Final panel"
      />
      <div className="grid grid-cols-2 gap-2.5">
        <input
          className={field}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          placeholder="Date — e.g. 12 Jul 2026"
        />
        <MiniSelect
          ariaLabel="Format"
          value={format}
          onChange={setFormat}
          options={INTERVIEW_FORMAT_OPTIONS}
          fullWidth
        />
      </div>
      <input
        className={field}
        value={people}
        onChange={(e) => setPeople(e.target.value)}
        placeholder="Interviewers — comma separated"
      />
      <textarea
        className={cn(field, "resize-none")}
        rows={3}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="Feedback / notes"
      />
      {/* Recommendation — the round's verdict — as a full-width segmented control: three equal cells fill the
          row (no dropdown stranded against empty space), and the whole set of options is visible at a glance.
          Selected cell uses the app's inverted "selected" pill, matching the other segmented controls. */}
      <div className="space-y-1.5 pt-0.5">
        <span className="field-label">Recommendation</span>
        <div className="inline-flex w-full rounded-lg bg-muted p-0.5">
          {RECOMMENDATION_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                setRec(o.value);
                onRecChange?.(o.value);
              }}
              aria-pressed={rec === o.value}
              className={cn(
                "inline-flex flex-1 items-center justify-center rounded-md py-1.5 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                rec === o.value
                  ? "bg-foreground text-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

type Tab = "info" | "journey";

/** Read-only candidate detail as a right-side slide-over — the client's "look at this person" surface,
 *  replacing a full page so the slate stays behind it (prev/next pages through without closing). Built on
 *  Radix Dialog for focus-trap / Esc / scrim / scroll-lock for free.
 *
 *  Layout: a tight identity header, underline tabs, then the product's ONE section system — every
 *  section is a `rounded-xl border bg-card` panel (like the candidate page this replaces), separated by
 *  whitespace, no divider rules, no coloured edge-strips. Reading order is deliberate: who they are
 *  (header) → why they're strong (assessment) → practical facts & comp → any other roles they're up
 *  for → artifacts. Direct contact (email/phone) is the one field shielded from the client — the search
 *  firm mediates contact. The identity header renders instantly from the row's card; the body streams in
 *  with the profile fetch. */
export function CandidateDrawer({
  candidateId,
  searchId,
  ladder,
  card,
  order,
  onNavigate,
}: {
  candidateId: string;
  searchId: string;
  ladder: StageDef[];
  /** the row's card for THIS role — supplies stage / status / days-in-stage instantly */
  card: CandidateCard | null;
  /** candidateIds in the currently-displayed order, for prev/next + "n of m" */
  order: string[];
  /** navigate the drawer: an id switches candidate, null closes it */
  onNavigate: (candidateId: string | null) => void;
}) {
  const [tab, setTab] = useState<Tab>("info");
  // Admin mode turns the same drawer editable; "Preview as client" flips it back to the read-only render.
  // The Edit ⇄ Preview state is SHARED with the role page (one context) so the two are always in lockstep,
  // and admins land in Preview by default.
  const role = useClientRole();
  const isAdmin = role === "admin";
  const { previewClient, toggle: togglePreview } = useEditPreview();
  const editing = isAdmin && !previewClient;
  // Session-local edits for the design mock — held here, not persisted (wiring is a dev follow-up).
  const [ov, setOv] = useState<Record<string, string>>({});
  const put = (k: string) => (v: string) => setOv((o) => ({ ...o, [k]: v }));
  const get = (k: string, fallback?: string | null) => ov[k] ?? fallback ?? "";
  const [draftStage, setDraftStage] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<CandidateStatus | null>(null);
  const [removedDocs, setRemovedDocs] = useState<Set<string>>(() => new Set());
  // Session-local uploads (the design mock — the file itself isn't stored; dev wires real storage).
  const [uploadOpen, setUploadOpen] = useState(false);
  const [addedDocs, setAddedDocs] = useState<ProfileDocument[]>([]);
  const handleAddDoc = (v: { fileName: string; byteSize: number; docType: string }) => {
    setAddedDocs((prev) => [
      {
        id: crypto.randomUUID(),
        fileName: v.fileName,
        docType: v.docType,
        version: 1,
        byteSize: v.byteSize,
        uploadedAt: new Date().toISOString(),
      },
      ...prev,
    ]);
  };
  // Session-local role links (the mock — the real graph edge is a dev follow-up).
  const [linkOpen, setLinkOpen] = useState(false);
  const [addedEdges, setAddedEdges] = useState<ProfileEdge[]>([]);
  const handleLinkRole = (v: { searchId: string; searchTitle: string; searchTier: Tier }) => {
    setAddedEdges((prev) => [
      {
        id: crypto.randomUUID(),
        searchId: v.searchId,
        searchTitle: v.searchTitle,
        searchTier: v.searchTier,
        stage: "",
        stageLabel: "Long list",
        stageIndex: 0,
        stageTotal: 8,
        status: "active",
      },
      ...prev,
    ]);
  };
  const {
    data: profile,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["client", "candidate", candidateId],
    queryFn: () => clientApi.candidate(candidateId),
  });

  const idx = order.indexOf(candidateId);
  const prevId = idx > 0 ? (order[idx - 1] ?? null) : null;
  const nextId = idx >= 0 && idx < order.length - 1 ? (order[idx + 1] ?? null) : null;

  // Identity + stage read from the card first (instant), falling back to the profile once it lands.
  const fullName = card?.fullName ?? profile?.fullName ?? "Candidate";
  const title = card?.currentTitle ?? profile?.currentTitle ?? null;
  const employer = card?.currentEmployer ?? profile?.currentEmployer ?? null;
  const status = card?.status ?? "active";
  const stageKey = card?.stage ?? null;
  const curIdx = stageKey ? ladder.findIndex((s) => s.key === stageKey) : -1;
  const daysInStage = card?.daysInStage ?? null;
  const stalled = status === "active" && daysInStage != null && daysInStage > STALL_DAYS;

  // Admin's live stage/status edits (mock — reflected in the header + journey without persisting).
  const curStage = draftStage ?? stageKey;
  const curStatus = draftStatus ?? status;
  // Representative internal data (the client read-model shields contact; these are illustrative for the mock).
  const nameParts = fullName.toLowerCase().split(/\s+/).filter(Boolean);
  const mockEmail = `${nameParts[0] ?? "name"}.${nameParts[nameParts.length - 1] ?? "x"}@example.com`;
  const mockPhone = "+1 (415) 555-0148";
  const mockNotes = "Strong chemistry with the CEO; keen on the mandate. Comp expectation flagged early — within band.";
  const mockWaiting = "Client feedback after the final panel.";

  const subtitle = [title && employer ? `${title} · ${employer}` : (title ?? employer), profile?.location]
    .filter(Boolean)
    .join(" · ");

  // Snapshot facts — comp, availability, and sourcing. Comp leads: at a client evaluating a hire it's
  // the closeability question. Direct contact (email/phone) is the one field intentionally omitted from
  // the client view (the search firm mediates contact).
  const facts: { label: string; value: React.ReactNode }[] = [];
  if (profile?.currentSalary != null)
    facts.push({ label: "Base salary", value: formatMoney(profile.currentSalary, profile.currency) });
  if (profile?.totalComp != null)
    facts.push({ label: "Total comp", value: formatMoney(profile.totalComp, profile.currency) });
  if (profile?.salaryExpectation != null)
    facts.push({ label: "Comp expectation", value: formatMoney(profile.salaryExpectation, profile.currency) });
  if (profile?.noticePeriodDays != null)
    facts.push({ label: "Notice period", value: `${profile.noticePeriodDays} days` });
  if (profile?.earliestStart) facts.push({ label: "Earliest start", value: formatDate(profile.earliestStart) });
  if (profile?.source) facts.push({ label: "Source", value: profile.source });

  // CV first, then the rest — the résumé is the artifact a sponsor reaches for (Greenhouse surfaces it
  // first), so it leads the list.
  // Session-added uploads ride at the front, then the stable CV-first sort floats any résumé to the top.
  const docs = [...addedDocs, ...(profile?.documents ?? [])].sort(
    (a, b) => (a.docType === "cv" ? -1 : 0) - (b.docType === "cv" ? -1 : 0),
  );
  // Evaluations for THIS role, oldest-first so the timeline reads as a journey (undated rounds sink to
  // the end) — matching the left-to-right direction of the pipeline rail above it.
  const roleInterviews = (profile?.interviews.filter((iv) => iv.searchId === searchId) ?? []).slice().sort((a, b) => {
    const ta = a.dateConducted ? Date.parse(a.dateConducted) : Number.POSITIVE_INFINITY;
    const tb = b.dateConducted ? Date.parse(b.dateConducted) : Number.POSITIVE_INFINITY;
    return ta - tb;
  });
  // The candidate's OTHER roles in this client's portfolio (the current one is already the whole
  // context of the drawer, so it's excluded). Session-added links ride at the front.
  const otherRoles = [...addedEdges, ...(profile?.edges.filter((e) => e.searchId !== searchId) ?? [])];
  // Roles this candidate is already in (current + linked + just-added) — excluded from the Link-role picker.
  const linkedIds = new Set<string>([
    searchId,
    ...(profile?.edges.map((e) => e.searchId) ?? []),
    ...addedEdges.map((e) => e.searchId),
  ]);
  const hasInfo =
    !!(profile?.strengths || profile?.watchouts) || facts.length > 0 || otherRoles.length > 0 || docs.length > 0;

  const navBtn =
    "grid size-6 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:text-muted-foreground/25";

  return (
    <>
      <DialogPrimitive.Root open onOpenChange={(o) => (o ? undefined : onNavigate(null))}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out data-[state=open]:fade-in" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 right-0 z-50 flex h-full w-[520px] max-w-[92vw] flex-col border-l bg-background shadow-2xl outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-150 data-[state=open]:duration-200"
          >
            {/* Header — tight identity block. Slim nav bar, then name+status, subtitle, a compact stage
              line, then a segmented control for the two views; a hairline closes the header off. */}
            <div className="shrink-0 border-b border-border">
              <div className="flex h-11 items-center justify-between gap-2 px-5">
                <div className="flex items-center gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => prevId && onNavigate(prevId)}
                    disabled={!prevId}
                    aria-label="Previous candidate"
                    className={navBtn}
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  {order.length > 1 && idx >= 0 ? (
                    <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                      <span className="text-foreground">{idx + 1}</span> / {order.length}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => nextId && onNavigate(nextId)}
                    disabled={!nextId}
                    aria-label="Next candidate"
                    className={navBtn}
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin ? <EditPreviewToggle editing={editing} onToggle={togglePreview} /> : null}
                  <DialogPrimitive.Close
                    aria-label="Close"
                    className="grid size-7 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X className="size-4" />
                  </DialogPrimitive.Close>
                </div>
              </div>

              <div className="px-5 pb-4 pt-1">
                {/* Status is the one control the header carries — it's the lifecycle/health signal, and a
                    single control balances the name without the scattered feeling two of them created. Stage
                    (pipeline position) is NOT here: it's owned by the Journey tab, where you advance it by
                    moving along the timeline — so the header states health, the Journey owns progression. */}
                <div className="flex items-center justify-between gap-3">
                  <DialogPrimitive.Title className="truncate text-lg font-semibold leading-tight tracking-tight">
                    {fullName}
                  </DialogPrimitive.Title>
                  <div className="shrink-0">
                    {editing ? (
                      <MiniSelect
                        ariaLabel="Status"
                        value={curStatus}
                        onChange={(v) => setDraftStatus(v as CandidateStatus)}
                        options={STATUS_OPTIONS}
                      />
                    ) : (
                      <StatusPill value={status} />
                    )}
                  </div>
                </div>
                {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
              </div>

              <div className="px-5 pb-3">
                <div role="tablist" aria-label="Candidate detail" className="flex gap-0.5 rounded-lg bg-muted p-0.5">
                  {(["info", "journey"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="tab"
                      aria-selected={tab === t}
                      onClick={() => setTab(t)}
                      className={cn(
                        "flex-1 rounded-md px-3.5 py-1.5 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                        tab === t ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {t === "info" ? "Info" : "Journey"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {isError ? (
                <p className="px-5 py-8 text-sm text-muted-foreground">Couldn't load this candidate.</p>
              ) : tab === "info" ? (
                isLoading ? (
                  <div className="space-y-4 px-5 py-5">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                    <div className="h-14 w-full animate-pulse rounded bg-muted" />
                    <div className="h-14 w-full animate-pulse rounded bg-muted" />
                  </div>
                ) : editing ? (
                  <div className="space-y-3 p-4">
                    <Section label="Assessment">
                      <div className="space-y-4">
                        <div>
                          <span className="eyebrow text-[var(--color-success)]">Strengths</span>
                          <div className="mt-1.5">
                            <EditableField
                              value={get("strengths", profile?.strengths)}
                              onCommit={put("strengths")}
                              multiline
                              placeholder="Add strengths…"
                              className="text-foreground/90"
                            />
                          </div>
                        </div>
                        <div>
                          <span className="eyebrow text-[var(--color-warning)]">Watch-outs</span>
                          <div className="mt-1.5">
                            <EditableField
                              value={get("watchouts", profile?.watchouts)}
                              onCommit={put("watchouts")}
                              multiline
                              placeholder="Add watch-outs…"
                              className="text-foreground/90"
                            />
                          </div>
                        </div>
                      </div>
                    </Section>

                    <Section label="Details">
                      <dl className="space-y-1">
                        {[
                          {
                            k: "baseSalary",
                            label: "Base salary",
                            seed:
                              profile?.currentSalary != null
                                ? formatMoney(profile.currentSalary, profile.currency)
                                : "",
                            ph: "Add base salary…",
                          },
                          {
                            k: "totalComp",
                            label: "Total comp",
                            seed: profile?.totalComp != null ? formatMoney(profile.totalComp, profile.currency) : "",
                            ph: "Add total comp…",
                          },
                          {
                            k: "compExp",
                            label: "Comp expectation",
                            seed:
                              profile?.salaryExpectation != null
                                ? formatMoney(profile.salaryExpectation, profile.currency)
                                : "",
                            ph: "Add expectation…",
                          },
                          {
                            k: "notice",
                            label: "Notice period",
                            seed: profile?.noticePeriodDays != null ? `${profile.noticePeriodDays} days` : "",
                            ph: "Add notice…",
                          },
                          {
                            k: "start",
                            label: "Earliest start",
                            seed: profile?.earliestStart ? formatDate(profile.earliestStart) : "",
                            ph: "Add start date…",
                          },
                          { k: "source", label: "Source", seed: profile?.source ?? "", ph: "Add source…" },
                        ].map((f) => (
                          <div key={f.k} className="flex items-baseline justify-between gap-4">
                            <dt className="field-label shrink-0 pt-1">{f.label}</dt>
                            <div className="min-w-0 max-w-[60%] flex-1">
                              <EditableField value={get(f.k, f.seed)} onCommit={put(f.k)} placeholder={f.ph} />
                            </div>
                          </div>
                        ))}
                      </dl>
                    </Section>

                    <Section label="Contact">
                      <dl className="space-y-1">
                        {[
                          { k: "email", label: "Email", seed: mockEmail },
                          { k: "phone", label: "Phone", seed: mockPhone },
                        ].map((f) => (
                          <div key={f.k} className="flex items-baseline justify-between gap-4">
                            <dt className="field-label shrink-0 pt-1">{f.label}</dt>
                            <div className="min-w-0 max-w-[60%] flex-1">
                              <EditableField value={get(f.k, f.seed)} onCommit={put(f.k)} />
                            </div>
                          </div>
                        ))}
                      </dl>
                    </Section>

                    <Section label="Internal notes">
                      <div className="space-y-4">
                        <EditableField
                          value={get("notes", mockNotes)}
                          onCommit={put("notes")}
                          multiline
                          placeholder="Add a private note…"
                          className="text-foreground/90"
                        />
                        <div>
                          <span className="eyebrow">Waiting on</span>
                          <div className="mt-1.5">
                            <EditableField
                              value={get("waiting", mockWaiting)}
                              onCommit={put("waiting")}
                              placeholder="Nothing outstanding"
                              className="text-foreground/90"
                            />
                          </div>
                        </div>
                      </div>
                    </Section>

                    <Section
                      label="Documents"
                      meta={
                        uploadOpen ? undefined : (
                          <SectionAction icon={Upload} label="Upload" onClick={() => setUploadOpen(true)} />
                        )
                      }
                    >
                      <div className="space-y-2">
                        <div className="space-y-0.5">
                          {docs
                            .filter((d) => !removedDocs.has(d.id))
                            .map((d) => (
                              <EditableDocRow
                                key={d.id}
                                d={d}
                                onRemove={() => setRemovedDocs((s) => new Set(s).add(d.id))}
                              />
                            ))}
                        </div>
                        {/* Upload expands INLINE here — no modal stacked over the drawer. */}
                        {uploadOpen ? (
                          <UploadDocForm onAdd={handleAddDoc} onClose={() => setUploadOpen(false)} />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setUploadOpen(true)}
                            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-3 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
                          >
                            <Plus className="size-3.5" /> Add CV or supporting document
                          </button>
                        )}
                      </div>
                    </Section>

                    <Section
                      label="Other roles"
                      meta={
                        linkOpen ? undefined : (
                          <SectionAction icon={Plus} label="Link role" onClick={() => setLinkOpen(true)} />
                        )
                      }
                    >
                      <div className="space-y-3">
                        {otherRoles.length > 0 ? (
                          <div className="space-y-3">
                            {otherRoles.map((e) => (
                              <OtherRoleRow key={e.id} e={e} />
                            ))}
                          </div>
                        ) : !linkOpen ? (
                          <p className="py-1 text-sm text-muted-foreground">Not linked to any other role yet.</p>
                        ) : null}
                        {/* Link role expands INLINE here — no modal stacked over the drawer. */}
                        {linkOpen ? (
                          <LinkRoleForm
                            linkedIds={linkedIds}
                            onLink={handleLinkRole}
                            onClose={() => setLinkOpen(false)}
                          />
                        ) : null}
                      </div>
                    </Section>
                  </div>
                ) : !hasInfo ? (
                  <p className="px-5 py-8 text-sm text-muted-foreground">No further details shared yet.</p>
                ) : (
                  <div className="space-y-3 p-4">
                    {profile?.strengths || profile?.watchouts ? (
                      <Section label="Assessment">
                        <div className="space-y-4">
                          {profile?.strengths ? (
                            <div>
                              <span className="eyebrow text-[var(--color-success)]">Strengths</span>
                              <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground/90">
                                {profile.strengths}
                              </p>
                            </div>
                          ) : null}
                          {profile?.watchouts ? (
                            <div>
                              <span className="eyebrow text-[var(--color-warning)]">Watch-outs</span>
                              <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground/90">
                                {profile.watchouts}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </Section>
                    ) : null}

                    {facts.length > 0 ? (
                      <Section label="Details">
                        <dl className="space-y-2.5">
                          {facts.map((f) => (
                            <div key={f.label} className="flex items-baseline justify-between gap-4">
                              <dt className="field-label">{f.label}</dt>
                              <dd className="text-right text-sm font-medium tabular-nums text-foreground">{f.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </Section>
                    ) : null}

                    {otherRoles.length > 0 ? (
                      <Section label="Other roles">
                        <div className="space-y-3">
                          {otherRoles.map((e) => (
                            <OtherRoleRow key={e.id} e={e} />
                          ))}
                        </div>
                      </Section>
                    ) : null}

                    {docs.length > 0 ? (
                      <Section label="Documents">
                        <div className="space-y-0.5">
                          {docs.map((d) => (
                            <DocRow key={d.id} d={d} />
                          ))}
                        </div>
                      </Section>
                    ) : null}
                  </div>
                )
              ) : (
                <div className="p-4">
                  {isLoading ? (
                    <div className="space-y-3 rounded-xl border bg-card p-4">
                      <div className="h-5 w-24 animate-pulse rounded bg-muted" />
                      <div className="h-32 w-full animate-pulse rounded bg-muted" />
                    </div>
                  ) : (
                    <UnifiedJourney
                      ladder={ladder}
                      currentKey={curStage}
                      status={curStatus}
                      daysInStage={daysInStage}
                      stalled={stalled}
                      interviews={roleInterviews}
                      source={profile?.source ?? null}
                      editing={editing}
                      onSetStage={editing ? (k) => setDraftStage(k) : undefined}
                      onSetStatus={editing ? (s) => setDraftStatus(s) : undefined}
                    />
                  )}
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
